import { ActiveOrganization, useSubscriptionOfferCreate } from "@/api";
import { useToast } from "@/components/ui/use-toast";
import { SubscriptionOfferCreateMutationVariables } from "@/gql/__generated__/graphql";
import { TRIAL_DAYS } from "@/utils/subscriptionOfferTrialDays";
import { useNavigate } from "@tanstack/react-router";
import { FormEvent } from "react";

export function useHandleSubmitSubscriptionOffer(
  organization?: ActiveOrganization
) {
  const [subscriptionOfferCreate, { loading }] = useSubscriptionOfferCreate(
    organization?.slug || ""
  );
  const navigate = useNavigate();
  const { toast } = useToast();

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    const appSlug = (formData.get("app") as string) || undefined;
    const email = (formData.get("email") as string) || undefined;
    const message = (formData.get("message") as string) || undefined;
    const reference = (formData.get("reference") as string) || undefined;
    const expiresAt = (formData.get("expires-at") as string) || undefined;

    const price = formData.get("price")
      ? parseFloat(formData.get("price") as string)
      : undefined;
    const trialEnabled = formData.get("trial-toggle") === "on";

    if (!organization?.id || !appSlug || !email) return;

    let variables: SubscriptionOfferCreateMutationVariables = {
      organizationId: organization.id,
      appSlug,
      email,
      message,
      name: reference
    };

    if (expiresAt) {
      variables.expiresAt = new Date(expiresAt);
    }

    if (price !== undefined) {
      if (price < 10) {
        toast({
          title: "Subscription fee must be greater than $10",
          variant: "destructive"
        });
        return;
      }

      variables.payment = {
        monthlyPriceUSD: price.toString(),
        trialDays: trialEnabled ? TRIAL_DAYS : null
      };
    }

    subscriptionOfferCreate({ variables })
      .then((res) => {
        switch (res.data?.subscriptionOfferCreate.__typename) {
          case "InvalidEmail":
            toast({
              title: "Invalid email",
              description:
                "The customer email you have entered is invalid. Please input a valid email and try again.",
              variant: "destructive"
            });
            break;

          case "SubscriptionOffer":
            toast({
              title: "Subscription offer sent"
            });
            navigate({
              to: "/organization/$organizationSlug/subscriptions",
              params: (prev) => ({
                organizationSlug: prev.organizationSlug || ""
              }),
              search: {
                view: "subscription-offers"
              }
            });
            break;

          case "OfferInvalidExpiresAt":
            toast({
              title: "Invalid expiration date",
              description:
                "The expiration date you have entered is invalid. Please input a valid date and try again.",
              variant: "destructive"
            });
            break;

          case "OfferInvalidMonthlyPriceUSD":
            toast({
              title: "Invalid subscription fee",
              description:
                "The subscription fee you have entered is invalid. Please input a valid fee and try again.",
              variant: "destructive"
            });
            break;

          case "OfferInvalidTrialDays":
            toast({
              title: "Invalid trial days",
              description:
                "The trial days you have entered is invalid. Please input a valid number of days and try again.",
              variant: "destructive"
            });
            break;

          default:
            break;
        }
      })
      .catch((mutationError) => {
        console.error(
          "Create subscription offer mutation error",
          mutationError
        );
        toast({
          title: "Unable to create subscription offer",
          description:
            "An error occurred while creating the subscription offer. Please try again or report the issue",
          variant: "destructive"
        });
      });
  }

  return { handleSubmit, loading };
}
